import React, {useEffect, useState} from 'react';
import { Link } from 'gatsby';
import { MapPin, Phone, Mail } from 'lucide-react';
import { X } from 'lucide-react';
import { IoMdArrowDropdown } from "react-icons/io";
import axios from 'axios';
import { environment } from "@/environments/environment.ts";

export default function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [navs, setNavs] = useState<any[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [currentPath, setCurrentPath] = useState('');

    useEffect(() => {
        setCurrentPath(window.location.pathname);
    }, []);

    const fetchNavs = async () => {
        try {
            const response = await axios.get(`${environment.urlBase}/api/info`);
            setNavs(response.data.navs);
        } catch (error) {
            console.error('Error fetching navs:', error);
        }
    };

    useEffect(() => {
        fetchNavs();
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const closeMenu = () => {
        setMenuOpen(false);
    };

    const isActive = (path: string) => {
        if (path === '/') {
            return currentPath === path;
        }
        return currentPath.startsWith(path);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    // Cerrar el menú móvil cuando cambia la ruta
    useEffect(() => {
        setMenuOpen(false);
    }, [currentPath]);

    // Actualizar la ruta actual cuando cambia la URL
    useEffect(() => {
        const handleRouteChange = () => {
            setCurrentPath(window.location.pathname);
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => {
            window.removeEventListener('popstate', handleRouteChange);
        };
    }, []);

    return (
        <>
            <div className="bg-[#e7323a] text-white py-2 px-4 p-5 hidden lg:block z-40">
                <div className="container mx-auto flex flex-wrap justify-between items-center text-sm">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                            <MapPin size={16} className="mr-1"/>
                            <span> cll7 No 10B-100 Barrio Garzones, Montería - Colombia</span>
                        </div>
                        <div className="flex items-center">
                            <Phone size={16} className="mr-1"/>
                            <span>(+57) 3122862474</span>
                        </div>
                        <div className="flex items-center">
                            <Mail size={16} className="mr-1"/>
                            <span>recaudarsinu@gmail.com</span>
                        </div>
                    </div>
                </div>
            </div>

            <header className="sticky top-0 left-0 right-0 bg-white text-gray-800 p-2 w-full shadow-lg z-50 transition-all duration-300">
                <div className="container mx-auto flex justify-between items-center">
                    <Link to="/" onClick={() => setCurrentPath('/')}>
                        <img src={'/logo-recaudar.png'} alt="Logo" className="w-16 h-16"/>
                    </Link>

                    <button
                        onClick={toggleMenu}
                        className="lg:hidden focus:outline-none"
                        aria-label="Toggle Menu"
                        aria-expanded={menuOpen}
                    >
                        <svg className="w-6 h-6 fill-current text-gray-800" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24">
                            <path fillRule="evenodd" d="M4 5h16v2H4V5zm0 6h16v2H4v-2zm0 6h16v2H4v-2z"/>
                        </svg>
                    </button>

                    <nav className="hidden lg:flex items-center space-x-8">
                        {[
                            {path: '/', label: 'Inicio'},
                            {path: '/nosotros', label: 'Nosotros'},
                            {path: '/rutas', label: 'Rutas'},
                            {path: '/pqr', label: 'Atención al Cliente'},
                            {path: '/blog', label: 'Blog'},
                            {path: '/trabajaconrecaudar', label: 'Trabaja Con Nosotros'},
                        ].map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setCurrentPath(item.path)}
                                className={`relative group py-2 hover:text-[#e7323a] transition duration-300 ${
                                    isActive(item.path) ? 'text-[#e7323a] font-semibold' : 'text-gray-700'
                                }`}
                            >
                                {item.label}
                                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#e7323a] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100 ${
                                    isActive(item.path) ? 'scale-x-100' : ''
                                }`}></span>
                            </Link>
                        ))}
                        <div className="relative inline-block text-left">
                            <button
                                type="button"
                                onClick={toggleDropdown}
                                className="inline-flex items-center gap-x-1.5 px-3 py-2 text-gray-700 hover:text-[#e7323a] transition duration-300 group"
                            >
                                Nuestras Aplicaciones
                                <IoMdArrowDropdown className={`inline ml-1 h-5 w-5 text-gray-400 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown menu */}
                            {dropdownOpen && (
                                <div className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 transform transition-all duration-200 ease-out">
                                    <div className="py-2">
                                        {/* Sección de iconos de aplicaciones */}
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <div className="grid grid-cols-2 gap-4">
                                                <Link
                                                    to="/apps/movisinu"
                                                    className="flex flex-col items-center p-3 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                                                >
                                                    <img 
                                                        src="/icons/our-apps/movisinu.png" 
                                                        alt="movisinú" 
                                                        className="w-12 h-12 object-contain mb-2 group-hover:scale-110 transition-transform duration-200"
                                                    />
                                                    <span className="text-xs text-gray-700 group-hover:text-[#e7323a] font-medium text-center">
                                                        movisinú
                                                    </span>
                                                </Link>
                                                <a
                                                    href="#"
                                                    className="flex flex-col items-center p-3 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                                                >
                                                    <img 
                                                        src="/icons/our-apps/optra.png" 
                                                        alt="optra" 
                                                        className="w-12 h-12 object-contain mb-2 group-hover:scale-110 transition-transform duration-200"
                                                    />
                                                    <span className="text-xs text-gray-700 group-hover:text-[#e7323a] font-medium text-center">
                                                        optra
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                        {/* Sección de enlaces */}
                                        <div className="py-2">
                                            {navs.map((nav) => (
                                                <a
                                                    key={nav.link}
                                                    href={nav.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-[#e7323a] transition-colors duration-200"
                                                >
                                                    {nav.nombre}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </nav>
                </div>
            </header>

            {/* Menú móvil */}
            <div
                className={`lg:hidden fixed top-0 left-0 w-full h-full bg-white z-50 transition-transform duration-300 ease-in-out ${
                    menuOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex justify-end p-4">
                    <button onClick={closeMenu} className="focus:outline-none">
                        <X size={24} className="text-gray-800"/>
                    </button>
                </div>

                <nav className="flex flex-col items-center space-y-6 mt-10">
                    {[
                        {path: '/', label: 'Inicio'},
                        {path: '/nosotros', label: 'Nosotros'},
                        {path: '/rutas', label: 'Rutas'},
                        {path: '/pqr', label: 'Atención al Cliente'},
                        {path: '/blog', label: 'Blog'},
                        {path: '/trabajaconrecaudar', label: 'Trabaja Con Nosotros'},
                    ].map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => {
                                setCurrentPath(item.path);
                                closeMenu();
                            }}
                            className={`text-xl hover:text-[#e7323a] transition duration-300 ${
                                isActive(item.path) ? 'text-[#e7323a] font-semibold' : ''
                            }`}
                        >
                            {item.label}
                        </Link>
                    ))}
                    {/* Menú desplegable de Nuestras Aplicaciones */}
                    <div className="relative">
                        <div
                            className={`flex items-center cursor-pointer text-xl hover:text-[#e7323a] transition duration-300`}
                            onClick={toggleDropdown}
                        >
                            Nuestras Aplicaciones
                            <IoMdArrowDropdown className="ml-1 h-5 w-5 text-gray-400"/>
                        </div>

                        {/* Dropdown menu */}
                        {dropdownOpen && (
                            <div className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                <div className="py-1">
                                    {/* Sección de iconos de aplicaciones */}
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <div className="grid grid-cols-2 gap-4">
                                            <Link
                                                to="/apps/movisinu"
                                                className="flex flex-col items-center p-3 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                                            >
                                                <img 
                                                    src="/icons/our-apps/movisinu.png" 
                                                    alt="movisinú" 
                                                    className="w-12 h-12 object-contain mb-2 group-hover:scale-110 transition-transform duration-200"
                                                />
                                                <span className="text-xs text-gray-700 group-hover:text-[#e7323a] font-medium text-center">
                                                    movisinú
                                                </span>
                                            </Link>
                                            <a
                                                href="#"
                                                className="flex flex-col items-center p-3 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                                            >
                                                <img 
                                                    src="/icons/our-apps/optra.png" 
                                                    alt="optra" 
                                                    className="w-12 h-12 object-contain mb-2 group-hover:scale-110 transition-transform duration-200"
                                                />
                                                <span className="text-xs text-gray-700 group-hover:text-[#e7323a] font-medium text-center">
                                                    optra
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                    {/* Sección de enlaces */}
                                    <div className="py-1">
                                        {navs.map((nav) => (
                                            <a
                                                key={nav.id}
                                                href={nav.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                {nav.nombre}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </nav>
            </div>
        </>
    );
}
